import { useEffect, useState } from "react";
import { Plus, Search, MoreHorizontal, LayoutGrid, List } from "lucide-react";
import { useWorkspacesStore } from "@/stores/workspaces-store";
import { useDialog } from "@/hooks/useDialog";
import { WorkspaceCard } from "./WorkspaceCard";
import { WorkspaceCardSkeleton } from "./WorkspaceCardSkeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function WorkspacesPage() {
  const { workspaces, loading: isLoading, reload } = useWorkspacesStore();
  const { openDialog } = useDialog();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("edited");
  const [visibility, setVisibility] = useState("all");
  const [status, setStatus] = useState("all");
  const [creator, setCreator] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = search
    ? workspaces.filter((w) => w.name.toLowerCase().includes(search.toLowerCase()))
    : workspaces;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-foreground">Workspaces</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>Settings</DropdownMenuItem>
            <DropdownMenuItem>Export</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters Bar */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        {isLoading ? (
          <>
            <Skeleton className="h-9 flex-1 min-w-[200px] max-w-xs" />
            <Skeleton className="h-9 w-[140px]" />
            <Skeleton className="h-9 w-[140px]" />
            <Skeleton className="h-9 w-[130px]" />
            <Skeleton className="h-9 w-[130px]" />
            <Skeleton className="ml-auto h-9 w-[72px]" />
          </>
        ) : (
          <>
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search workspaces..."
                className="pl-9 h-9"
              />
            </div>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px] h-9">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="edited">Last edited</SelectItem>
                <SelectItem value="created">Date created</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>

            {/* Visibility Filter */}
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="w-[140px] h-9">
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any visibility</SelectItem>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="shared">Shared</SelectItem>
                <SelectItem value="public">Public</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[130px] h-9">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            {/* Creator Filter */}
            <Select value={creator} onValueChange={setCreator}>
              <SelectTrigger className="w-[130px] h-9">
                <SelectValue placeholder="Creator" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All creators</SelectItem>
                <SelectItem value="me">Me</SelectItem>
                <SelectItem value="others">Others</SelectItem>
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(v) => v && setViewMode(v as "grid" | "list")}
              className="ml-auto border rounded-md"
            >
              <ToggleGroupItem value="grid" aria-label="Grid view" className="h-8 w-8 p-0">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="List view" className="h-8 w-8 p-0">
                <List className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </>
        )}
      </div>

      {/* Workspaces Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <WorkspaceCardSkeleton key={i} />
          ))
        ) : (
          <>
            <Button
              variant="outline"
              onClick={() => openDialog("create-workspace")}
              className="flex aspect-[4/3] h-auto flex-col items-center justify-center gap-3 border-2 border-dashed text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground"
            >
              <Plus className="h-8 w-8 stroke-[1.5]" />
              <span className="text-sm font-medium">Create new workspace</span>
            </Button>
            {filtered.map((workspace) => (
              <WorkspaceCard key={workspace.id} workspace={workspace} />
            ))}
          </>
        )}
      </div>

      {/* No Results */}
      {!isLoading && filtered.length === 0 && search && (
        <p className="mt-8 text-center text-sm text-muted-foreground">
          No workspaces matching "{search}"
        </p>
      )}
    </div>
  );
}
